import React from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import OrderStatusBadge from './OrderStatusBadge';
import PaymentStatusBadge from './PaymentStatusBadge';

const OrderDetailsModal = ({ order, isOpen, onClose, onDownloadInvoice, onTrackOrder }) => {
  if (!isOpen || !order) return null;

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-UG', {
      style: 'currency',
      currency: 'UGX',
      minimumFractionDigits: 0
    })?.format(amount);
  };

  const formatDate = (date) => {
    return new Intl.DateTimeFormat('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })?.format(new Date(date));
  };

  const getProgressPercentage = (status) => {
    switch (status) {
      case 'pending': return 25;
      case 'in-progress': return 50;
      case 'shipped': return 75;
      case 'completed': return 100;
      default: return 0;
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
      <div className="bg-card border border-border rounded-lg shadow-elevated max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-border">
          <div className="flex items-center space-x-3">
            <Icon name="FileText" size={24} className="text-primary" />
            <div>
              <h2 className="text-xl font-semibold text-foreground">
                Order #{order?.orderNumber}
              </h2>
              <p className="text-sm text-muted-foreground">
                Placed on {formatDate(order?.orderDate)}
              </p>
            </div>
          </div>
          <Button variant="ghost" onClick={onClose} iconName="X" />
        </div>

        <div className="p-6 space-y-6">
          {/* Order Status & Progress */}
          <div className="bg-muted/30 rounded-lg p-4">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center space-x-4">
                <OrderStatusBadge status={order?.status} />
                <PaymentStatusBadge status={order?.paymentStatus} method={order?.paymentMethod} />
              </div>
              <div className="text-right">
                <div className="text-sm text-muted-foreground">Total Amount</div>
                <div className="text-xl font-bold text-foreground">
                  {formatCurrency(order?.totalAmount)}
                </div>
              </div>
            </div>

            {/* Progress Bar */}
            <div className="space-y-2">
              <div className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground">Order Progress</span>
                <span className="text-foreground font-medium">
                  {getProgressPercentage(order?.status)}%
                </span>
              </div>
              <div className="w-full bg-muted rounded-full h-2">
                <div 
                  className="bg-primary h-2 rounded-full transition-all duration-300"
                  style={{ width: `${getProgressPercentage(order?.status)}%` }}
                />
              </div>
            </div>
          </div>

          {/* Order Items */}
          <div>
            <h3 className="text-lg font-semibold text-foreground mb-4">Order Items</h3>
            <div className="space-y-3">
              {order?.items?.map((item, index) => (
                <div key={index} className="flex items-center justify-between p-4 bg-muted/20 rounded-lg">
                  <div className="flex-1">
                    <h4 className="font-medium text-foreground">{item?.name}</h4>
                    <p className="text-sm text-muted-foreground capitalize">
                      Category: {item?.category}
                    </p>
                    {item?.description && (
                      <p className="text-sm text-muted-foreground mt-1">
                        {item?.description}
                      </p>
                    )}
                  </div>
                  <div className="text-right">
                    <div className="font-semibold text-foreground">
                      {formatCurrency(item?.price)}
                    </div>
                    <div className="text-sm text-muted-foreground">
                      Qty: {item?.quantity || 1}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Payment Information */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <h3 className="text-lg font-semibold text-foreground mb-4">Payment Details</h3>
              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Payment Method:</span>
                  <span className="text-foreground font-medium">{order?.paymentMethod}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Payment Status:</span>
                  <PaymentStatusBadge status={order?.paymentStatus} size="sm" />
                </div>
                {order?.transactionId && (
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Transaction ID:</span>
                    <span className="text-foreground font-mono text-sm">{order?.transactionId}</span>
                  </div>
                )}
                {order?.paymentDate && (
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Payment Date:</span>
                    <span className="text-foreground">{formatDate(order?.paymentDate)}</span>
                  </div>
                )}
              </div>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-foreground mb-4">Delivery Information</h3>
              <div className="space-y-3">
                {order?.deliveryAddress && (
                  <div>
                    <span className="text-muted-foreground block">Delivery Address:</span>
                    <span className="text-foreground">{order?.deliveryAddress}</span>
                  </div>
                )}
                {order?.estimatedDelivery && (
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Estimated Delivery:</span>
                    <span className="text-foreground">{formatDate(order?.estimatedDelivery)}</span>
                  </div>
                )}
                {order?.trackingNumber && (
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Tracking Number:</span>
                    <span className="text-foreground font-mono text-sm">{order?.trackingNumber}</span>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Order Timeline */}
          {order?.timeline && (
            <div>
              <h3 className="text-lg font-semibold text-foreground mb-4">Order Timeline</h3>
              <div className="space-y-4">
                {order?.timeline?.map((event, index) => (
                  <div key={index} className="flex items-start space-x-3">
                    <div className="flex-shrink-0 w-8 h-8 bg-primary/10 rounded-full flex items-center justify-center">
                      <Icon name="Clock" size={16} className="text-primary" />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-medium text-foreground">{event?.title}</span>
                        <span className="text-xs text-muted-foreground">
                          {formatDate(event?.timestamp)}
                        </span>
                      </div>
                      {event?.description && (
                        <p className="text-sm text-muted-foreground mt-1">{event?.description}</p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Support Information */}
          {order?.supportTicket && (
            <div className="bg-accent/10 border border-accent/20 rounded-lg p-4">
              <div className="flex items-center space-x-2 mb-2">
                <Icon name="MessageCircle" size={16} className="text-accent" />
                <span className="text-sm font-medium text-foreground">Support Ticket</span>
              </div>
              <p className="text-sm text-muted-foreground">
                Ticket #{order?.supportTicket} - {order?.supportStatus || 'Open'}
              </p>
            </div>
          )}
        </div>

        {/* Footer Actions */}
        <div className="flex items-center justify-end space-x-3 p-6 border-t border-border bg-muted/20">
          <Button
            variant="outline"
            onClick={() => onDownloadInvoice(order)}
            iconName="Download"
            iconPosition="left"
          >
            Download Invoice
          </Button>
          <Button
            variant="outline"
            onClick={() => onTrackOrder(order)}
            iconName="MapPin"
            iconPosition="left"
          >
            Track Order
          </Button>
          <Button variant="default" onClick={onClose}>
            Close
          </Button>
        </div>
      </div>
    </div>
  );
};

export default OrderDetailsModal;